/* eslint-disable */
// @ts-nocheck
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  AddOrUpdateSurveyRequest,
  AddSurveyQuestionRequest,
  AnswerSurveyRequest,
  cropConnectApiClient,
  SurveyDtoCropConnectApiResponse,
  SurveyFormDtoCropConnectApiResponse,
  SurveyQuestionDtoCropConnectApiResponse,
  SurveyType,
} from '../services/cropConnectApiClient';

interface SurveyQuestion {
  accessId: string;
  surveyAccessId: string;
}

interface SurveyState {
  surveys: SurveyDtoCropConnectApiResponse[];
  surveyQuestions: SurveyQuestion[];
  loading: boolean;
  activeSurvey: any;
  activeSurveyForm: any;
  error?: string;
  currentSurveyAccessId: string | null;
  surveyDescription: string;
  surveyHint: {
    text: string;
    enabled: boolean;
  };
  surveyConsents: {
    mainConsent: string;
    extraConsent: string;
  };
  surveyAdditionalProperties: Record<string, any>;
  fetchSurveys: () => Promise<void>;
  fetchSurvey: (
    surveyId: string,
  ) => Promise<SurveyDtoCropConnectApiResponse | undefined>;
  createSurvey: (
    body: AddOrUpdateSurveyRequest,
  ) => Promise<SurveyDtoCropConnectApiResponse | void>;
  addSurveyQuestion: (
    surveyId: string,
    questionData: AddSurveyQuestionRequest,
  ) => Promise<SurveyQuestionDtoCropConnectApiResponse | void>;
  updateSurveyQuestion: (
    surveyId: string,
    questionId: string,
    questionData: AddSurveyQuestionRequest,
  ) => Promise<SurveyQuestionDtoCropConnectApiResponse | void>;
  deleteSurveyQuestion: (
    surveyId: string,
    questionId: string,
  ) => Promise<SurveyQuestionDtoCropConnectApiResponse | void>;
  setCurrentSurveyAccessId: (accessId: string | null) => void;
  clearCurrentSurveyAccessId: () => void;
  deleteSurvey: (surveyId: string) => Promise<void>;
  editSurvey: (
    surveyId: string,
    surveyData: AddOrUpdateSurveyRequest,
  ) => Promise<SurveyDtoCropConnectApiResponse | void>;
  toggleSurveyStatus: (
    surveyId: string,
    currentStatus: boolean,
  ) => Promise<SurveyDtoCropConnectApiResponse | void>;
  setSurveyDescription: (description: string) => void;
  setSurveyHint: (hint: { text: string; enabled: boolean }) => void;
  setSurveyConsents: (consents: {
    mainConsent: string;
    extraConsent: string;
  }) => void;
  fetchActiveSurvey: () => Promise<void>;
  getSurveyFormForSurvey: (surveyId: string) => Promise<void>;
  submitSurveyAnswers: (
    surveyId: string,
    body: AnswerSurveyRequest,
  ) => Promise<SurveyFormDtoCropConnectApiResponse | void>;
}

export const useSurveyRedCertStore = create<SurveyState>()(
  devtools((set) => ({
    surveys: [],
    surveyQuestions: [],
    loading: false,
    error: undefined,
    currentSurveyAccessId: null,
    activeSurvey: null,
    activeSurveyForm: null,
    surveyAdditionalProperties: {},
    surveyDescription: '',
    surveyHint: { text: '', enabled: false },

    fetchSurveys: async () => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.getSurveyByType(
          SurveyType.RedCert,
          100,
          0,
        );
        if (response) {
          const formattedSurveys = Array.isArray(response.data)
            ? response.data.map((survey) => ({ data: survey, success: true }))
            : [];

          set({
            surveys: formattedSurveys,
            loading: false,
          });
        }
      } catch (error) {
        console.error('Error fetching surveys:', error);
        set({ error: 'Failed to fetch surveys.', loading: false });
      }
    },

    fetchSurvey: async (surveyId: string) => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.getSurvey(surveyId);
        if (response?.data) {
          set((state) => {
            const defaultAdditionalProps = {
              hintForInvekos: '',
              consents: { mainConsent: '', extraConsent: '' },
              description: '',
              hint: { text: '', enabled: false },
            };

            const enrichedResponse = {
              ...response,
              data: {
                ...response.data,
                additionalProperties: {
                  ...defaultAdditionalProps,
                  ...response.data.additionalProperties,
                },
              },
            };

            return {
              surveys: state.surveys.map((s) =>
                s.data?.accessId === surveyId ? enrichedResponse : s,
              ),
              loading: false,
            };
          });
          return response;
        }
        return null;
      } catch (error) {
        console.error('Error fetching survey:', error);
        set({ error: 'Failed to fetch survey.', loading: false });
        return null;
      }
    },

    createSurvey: async (body: AddOrUpdateSurveyRequest) => {
      set({ loading: true });
      try {
        const updatedBody = {
          ...body,
          additionalProperties: {
            ...body.additionalProperties,
            consents: body.additionalProperties?.consents || {
              mainConsent: '',
              extraConsent: '',
            },
          },
        };

        const response = await cropConnectApiClient.addSurvey(updatedBody);
        if (response?.data) {
          set((state) => ({
            surveys: [...state.surveys, response],
            surveyAdditionalProperties: {
              ...state.surveyAdditionalProperties,
              [response.data.accessId]: updatedBody.additionalProperties,
            },
            loading: false,
          }));
          return response;
        }
      } catch (error) {
        console.error('Error creating survey:', error);
        set({ error: 'Failed to create survey.', loading: false });
      }
    },

    editSurvey: async (surveyId: string, surveyData: AddOrUpdateSurveyRequest) => {
      set({ loading: true });
      try {
        const currentSurvey = await cropConnectApiClient.getSurvey(surveyId);

        const formattedData = {
          ...currentSurvey?.data,
          ...surveyData,
          additionalProperties: {
            ...currentSurvey?.data?.additionalProperties,
            ...surveyData.additionalProperties,
          },
          type: SurveyType.RedCert,
        };

        const response = await cropConnectApiClient.updateSurvey(
          surveyId,
          formattedData,
        );

        if (response?.data) {
          set((state) => ({
            surveys: state.surveys.map((survey) =>
              survey.data.accessId === surveyId ? response : survey,
            ),
            loading: false,
          }));
          return response;
        }
      } catch (error) {
        console.error('Error editing survey:', error);
        set({ error: 'Failed to edit survey.', loading: false });
      }
    },

    addSurveyQuestion: async (surveyId, questionData) => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.addSurveyQuestion(
          surveyId,
          questionData,
        );

        if (response?.data) {
          set((state) => ({
            surveys: state.surveys.map((survey) => {
              if (survey.data.accessId === surveyId) {
                const newQuestions = [
                  ...(survey.data.questions || []),
                  response.data,
                ].sort((a, b) => a.properties.order - b.properties.order);

                return {
                  ...survey,
                  data: {
                    ...survey.data,
                    questions: newQuestions,
                  },
                };
              }
              return survey;
            }),
            loading: false,
          }));
          return response;
        }
      } catch (error) {
        console.error('Error adding survey question:', error);
        set({ error: 'Failed to add survey question.', loading: false });
      }
    },

    updateSurveyQuestion: async (surveyId, questionId, questionData) => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.updateSurveyQuestion(
          surveyId,
          questionId,
          questionData,
        );

        if (response?.data) {
          set((state) => ({
            surveys: state.surveys.map((survey) => {
              if (survey.data.accessId === surveyId) {
                return {
                  ...survey,
                  data: {
                    ...survey.data,
                    questions:
                      survey.data.questions?.map((q) =>
                        q.accessId === questionId ? response.data : q,
                      ) || [],
                  },
                };
              }
              return survey;
            }),
            loading: false,
          }));
          return response;
        }
      } catch (error) {
        console.error('Error updating survey question:', error);
        set({ error: 'Failed to update survey question.', loading: false });
      }
    },

    deleteSurveyQuestion: async (surveyId, questionId) => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.deleteSurveyQuestion(
          surveyId,
          questionId,
        );

        if (response?.data) {
          const updatedSurvey = await cropConnectApiClient.getSurvey(surveyId);
          if (updatedSurvey?.data) {
            set((state) => ({
              surveys: state.surveys.map((survey) =>
                survey.data.accessId === surveyId ? updatedSurvey : survey,
              ),
              loading: false,
            }));
          }
          return response;
        }
      } catch (error) {
        console.error('Error deleting survey question:', error);
        set({ error: 'Failed to delete survey question.', loading: false });
        throw error;
      }
    },

    deleteSurvey: async (surveyId: string) => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.deleteSurvey(surveyId);
        if (response?.success) {
          set((state) => ({
            surveys: state.surveys.filter(
              (survey) => survey.data.accessId !== surveyId,
            ),
            loading: false,
          }));
        }
      } catch (error) {
        console.error('Error deleting survey:', error);
        set({ error: 'Failed to delete survey.', loading: false });
      }
    },

    toggleSurveyStatus: async (surveyId: string, currentStatus: boolean) => {
      set({ loading: true });
      try {
        const survey = await cropConnectApiClient.getSurvey(surveyId);
        if (survey?.data) {
          const updatedSurvey = {
            ...survey.data,
            active: !currentStatus,
          };

          const response = await cropConnectApiClient.updateSurvey(
            surveyId,
            updatedSurvey,
          );

          if (response?.data) {
            set((state) => ({
              surveys: state.surveys.map((s) =>
                s.data.accessId === surveyId ? response : s,
              ),
              loading: false,
            }));
            return response;
          }
        }
      } catch (error) {
        console.error('Error toggling survey status:', error);
        set({ error: 'Failed to update survey status.', loading: false });
      }
    },

    fetchActiveSurvey: async () => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.getSurveyByType(
          SurveyType.RedCert,
          1,
          0,
        );

        if (response?.data) {
          const activeSurvey = Array.isArray(response.data)
            ? response.data.find((survey) => survey.active)
            : null;

          if (activeSurvey) {
            const fullSurveyForm =
              await cropConnectApiClient.getSurveyFormForSurvey(
                activeSurvey.accessId,
              );

            set({
              activeSurvey: { data: activeSurvey, success: true },
              activeSurveyForm: fullSurveyForm?.data || null,
              loading: false,
            });
          } else {
            set({
              activeSurvey: null,
              activeSurveyForm: null,
              loading: false,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching active survey:', error);
        set({
          error: 'Failed to fetch active survey.',
          loading: false,
          activeSurvey: null,
          activeSurveyForm: null,
        });
      }
    },

    getSurveyFormForSurvey: async (surveyId: string) => {
      set({ loading: true });
      try {
        if (!surveyId) {
          throw new Error('Survey ID is empty');
        }

        const response =
          await cropConnectApiClient.getSurveyFormForSurvey(surveyId);

        console.log('Odpowiedź z getSurveyFormForSurvey:', {
          success: response?.success,
          data: response?.data,
        });

        if (response?.data) {
          set({
            activeSurveyForm: response.data,
            loading: false,
          });
          return response;
        }
      } catch (error) {
        console.error('Error fetching survey form:', error);
        console.error('Szczegóły błędu:', {
          surveyId: surveyId,
          errorMessage:
            error instanceof Error ? error.message : 'Unknown error',
        });
        set({
          error: 'Failed to fetch survey form.',
          activeSurveyForm: null,
          loading: false,
        });
        throw error;
      }
    },

    submitSurveyAnswers: async (surveyId, body) => {
      set({ loading: true });
      try {
        const response = await cropConnectApiClient.answerSurveyForm(
          surveyId,
          body,
        );

        if (response?.data) {
          set({ loading: false });
          return response;
        }
      } catch (error) {
        console.error('Error submitting survey:', error);
        set({
          error: 'Failed to submit survey answers.',
          loading: false,
        });
        throw error;
      }
    },

    setCurrentSurveyAccessId: (accessId: string | null) =>
      set({ currentSurveyAccessId: accessId }),
    setSurveyDescription: (description: string) =>
      set({ surveyDescription: description }),
    setSurveyHint: (hint: { text: string; enabled: boolean }) =>
      set({ surveyHint: hint }),
    setSurveyAdditionalProperties: (surveyId: string, props: any) =>
      set((state) => ({
        surveyAdditionalProperties: {
          ...state.surveyAdditionalProperties,
          [surveyId]: props,
        },
      })),

    clearCurrentSurveyAccessId: () => set({ currentSurveyAccessId: null }),
  })),
);
